import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Stack } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import SearchInput from 'common/components/SearchInput';
import { useTranslation } from 'services/localization';
import { InventoryStatus, InventoryStatuses, SkuItem } from 'common/modules/inventory';
import { LoadingData } from 'services/types';
import { skuItemsToOptions, getInventoryStatusTranslation } from '../utils';
import * as UserSelectors from 'selectors/UserSelectors';
import InventorySearchBarSelect from './InventorySearchBarSelect';

interface Props {
	skuItems: LoadingData<SkuItem[]>;
	searchValue?: string;
	onSearchValueChange: (value: string) => void;
	selectedLocationIds?: string[];
	onSelectedLocationIdsChange: (value: string[]) => void;
	selectedSkuIds?: string[];
	onSelectedSkuIdsChange: (value: string[]) => void;
	statuses?: InventoryStatus[];
	onStatusesChange?: (statuses: InventoryStatus[]) => void;
}

const InventorySearchBar = (props: Props) => {
	const {
		searchValue,
		onSearchValueChange,
		selectedLocationIds,
		onSelectedLocationIdsChange,
		skuItems,
		selectedSkuIds,
		onSelectedSkuIdsChange,
		statuses,
		onStatusesChange,
	} = props;
	const classes = useStyles();
	const { t } = useTranslation();
	const locations = useSelector(UserSelectors.userLocations);
	const validSelectedLocationIds = (selectedLocationIds ?? []).filter((id) =>
		locations.some((location) => location.id === id),
	);
	const hasMultipleLocations = locations.length > 1;

	const skuOptions = skuItemsToOptions(skuItems.data ?? []);
	const validSelectedSkuIds = (selectedSkuIds ?? []).filter((id) =>
		skuOptions.some((option) => option.id === id),
	);
	const validSelectedStatuses = (statuses ?? []).filter((status) => !!InventoryStatuses[status]);

	return (
		<Stack direction={{ xs: 'column', md: 'row' }} className={classes.root}>
			<Box className={classes.searchBar}>
				<SearchInput
					searchValue={searchValue}
					className={classes.searchField}
					onChange={onSearchValueChange}
					textFieldProps={{
						placeholder: t('common:actions.search'),
						variant: 'outlined',
						size: 'small',
						InputProps: {
							classes: {
								notchedOutline: classes.noInputBorder,
							},
						},
					}}
					t={t}
				/>
			</Box>
			<Stack className={classes.searchActions} direction={{ xs: 'column', sm: 'row' }}>
				{hasMultipleLocations && (
					<InventorySearchBarSelect
						name={t('inventory.columns.store', 'Store')}
						value={validSelectedLocationIds}
						onChange={(e) => onSelectedLocationIdsChange(e.target.value as string[])}
						options={locations.map((location) => {
							return {
								id: location.id,
								text: location.name || location.address,
							};
						})}
					/>
				)}
				<InventorySearchBarSelect
					name={t('inventory.columns.skuCOde', 'SKU Code')}
					value={validSelectedSkuIds}
					onChange={(e) => onSelectedSkuIdsChange(e.target.value as string[])}
					options={skuOptions.map((option) => {
						return {
							id: option.id,
							text: option.label,
						};
					})}
				/>
				{statuses !== undefined && (
					<InventorySearchBarSelect
						name={t('inventory.columns.status', 'Status')}
						value={validSelectedStatuses}
						onChange={(e) => onStatusesChange?.(e.target.value as InventoryStatus[])}
						options={Object.keys(InventoryStatuses).map((status) => {
							return {
								id: status as InventoryStatus,
								text: getInventoryStatusTranslation(t, status as InventoryStatus),
							};
						})}
					/>
				)}
			</Stack>
		</Stack>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			backgroundColor: theme.palette.background.paper,
			border: `1px solid ${theme.palette.colors.tundra.main}`,
			borderRadius: 4,
		},
		searchBar: {
			flex: 1,
			height: 40,
			[theme.breakpoints.down('md')]: {
				borderBottom: `1px solid ${theme.palette.colors.tundra.main}`,
			},
		},
		searchActions: {},
		searchField: {
			flex: '1 1 auto',
			minWidth: 200,
		},
		noInputBorder: {
			border: 'none',
		},
		barSection: {
			borderLeft: `1px solid ${theme.palette.colors.tundra.main}`,
			display: 'flex',
			alignItems: 'center',
			overflow: 'hidden',
			flex: '0 1 auto',
			height: 40,
			[theme.breakpoints.down('md')]: {
				flex: 1,
				'&:first-child': {
					borderLeft: 'none',
				},
			},
			[theme.breakpoints.down('sm')]: {
				borderLeft: 'none',
			},
		},
		skuSelectRoot: {
			minWidth: 110,
		},
		badgeRoot: {
			height: '100%',
			verticalAlign: 'text-top',
		},
		badgeInvisible: {
			display: 'none',
		},
	}),
);

export default InventorySearchBar;
